// Corrections of recorded workshop values (Stage 3a slice 9, plan §D2 P8): the fieldset
// correctionDialog.vue uses for one result (workshopFieldset), the "Valore precedente" line, the
// History lines of workshop.corrected, the submit key and the error keys. Plain module with no
// Vue, so it runs under `node --test`. Labels are i18n keys; the API checks everything again and
// decides (Manager / Admin only, the recording rules for the new value). Stage 6 (waste on): the
// container is corrected by picking a waste container ({ containerId }); with waste off it stays
// text and every output is the same as before.

import {
  CONTAINER_MAX,
  REASON_MAX,
  WORKSHOP_BASES,
  localDateTime,
  parseQuantity,
  performedAtLimits,
} from "./workshopForm.mjs";

// Same list as the API (constants WORKSHOP_CORRECTION_FIELDS). The result type is not correctable.
export const WORKSHOP_CORRECTION_FIELDS = [
  "quantity",
  "container",
  "reason",
  "performedAt",
];

// Result types that take a quantity and a container (as when recording).
const VALUE_RESULTS = ["completed", "exception"];

// field -> the dialog's paths (the quantity's value, unit and basis are corrected together).
const GROUPS = {
  quantity: ["quantity.value", "quantity.unit", "quantity.basis"],
  container: ["container"],
  reason: ["reason"],
  performedAt: ["performedAt"],
};

// The fields of a result a Manager may correct (API workshop/rules.js correctableFields): the
// reason and the performed-at always; the quantity and the container where the task and the
// result type take them, or the result already has one. With waste on a battery result also
// takes a container (D4).
export function correctableFields(task, result, { wasteEnabled = false } = {}) {
  if (!task || !result) return [];
  const takesValues = VALUE_RESULTS.includes(result.result);
  return WORKSHOP_CORRECTION_FIELDS.filter((field) => {
    if (field === "quantity") {
      return (
        Boolean(result.quantity) || (takesValues && Boolean(task.quantity))
      );
    }
    if (field === "container") {
      return (
        Boolean(result.container) ||
        Boolean(result.containerId) ||
        (takesValues && task.containerRequired === true) ||
        (wasteEnabled && takesValues && Boolean(task.quantity))
      );
    }
    return true;
  });
}

// The label of a field as the task card shows it (a completed result's text is a note).
export function fieldLabelKey(field, result) {
  if (field === "quantity") return "workshop.checklist.quantity";
  if (field === "container") return "workshop.checklist.container";
  if (field === "performedAt") return "workshop.checklist.performed_at";
  if (field === "reason") {
    return result && result.result === "completed"
      ? "workshop.checklist.notes"
      : "workshop.checklist.reason";
  }
  return field;
}

const text = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const formatNumber = (value, locale) =>
  new Intl.NumberFormat(locale || "it", { maximumFractionDigits: 3 }).format(
    value,
  );

const formatTime = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// How to show a field's value: { empty: true }, { text }, { key } or { segments: [display] }
// (joined with " · "), e.g. a quantity "41 kg · Stimata".
export function fieldDisplay(field, value, { locale } = {}) {
  if (value === null || value === undefined || value === "") {
    return { empty: true };
  }
  if (field === "quantity") {
    const amount = {
      text: `${formatNumber(value.value, locale)} ${value.unit || ""}`.trim(),
    };
    return value.basis
      ? { segments: [amount, { key: `workshop.basis.${value.basis}` }] }
      : amount;
  }
  if (field === "performedAt") return { text: formatTime(value) };
  return { text: String(value) };
}

// A display as text; t is the i18n function.
export function displayText(display, t) {
  if (!display || display.empty) return "—";
  if (display.segments) {
    return display.segments.map((part) => displayText(part, t)).join(" · ");
  }
  return display.key ? t(display.key) : display.text;
}

// The request `value` of a field from the dialog's form: a quantity { value, unit, basis } (null
// when the value is blank: an optional quantity removed), trimmed text, or an ISO instant. With
// waste on the container is { containerId } (null: none).
export function correctionValue(field, form, { wasteEnabled = false } = {}) {
  const values = form || {};
  if (field === "container" && wasteEnabled) {
    return values.container ? { containerId: values.container } : null;
  }
  if (field === "quantity") {
    const amount = text(values["quantity.value"]);
    if (!amount) return null;
    return {
      value: parseQuantity(amount),
      unit: values["quantity.unit"] || null,
      basis: values["quantity.basis"] || null,
    };
  }
  if (field === "performedAt") {
    const date = new Date(values.performedAt || "");
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  return text(values[field]);
}

// The result's effective values, flat and keyed by the dialog's paths (`values`). With waste on
// the container path holds the linked containerId.
export function correctionValues(task, result, { wasteEnabled = false } = {}) {
  const quantity = result && result.quantity;
  const units = (task && task.quantity && task.quantity.units) || [];
  const at = result && result.performedAt ? new Date(result.performedAt) : null;
  return {
    "quantity.value": quantity ? String(quantity.value) : "",
    "quantity.unit": quantity ? quantity.unit : units[0] || null,
    "quantity.basis": quantity ? quantity.basis : null,
    container: wasteEnabled
      ? (result && result.containerId) || null
      : (result && result.container) || "",
    reason: (result && result.reason) || "",
    performedAt: at && !Number.isNaN(at.getTime()) ? localDateTime(at) : "",
  };
}

// What stops the preview and the save: null, "required" (only disables) or a
// workshop.form.errors code. limits are performedAtLimits ({ min, max }). With waste on,
// container is the picked pick-list row ({ id, unit }) for the unit check.
export function correctionProblem(
  field,
  form,
  { task, result, limits, wasteEnabled = false, container = null },
) {
  const values = form || {};
  const completed = result && result.result === "completed";
  if (field === "quantity") {
    const amount = text(values["quantity.value"]);
    const required =
      completed && Boolean(task && task.quantity && task.quantity.required);
    if (!amount) return required ? "quantity_required" : null;
    const number = parseQuantity(amount);
    if (Number.isNaN(number)) return "quantity_invalid";
    if (!(number > 0)) return "quantity_not_positive";
    const units = (task && task.quantity && task.quantity.units) || [];
    if (!units.includes(values["quantity.unit"]))
      return "quantity_unit_invalid";
    if (!WORKSHOP_BASES.includes(values["quantity.basis"])) {
      return "basis_required";
    }
    return null;
  }
  if (field === "container" && wasteEnabled) {
    if (!values.container) {
      return completed && task && task.containerRequired
        ? "container_required"
        : null;
    }
    const quantity = result && result.quantity;
    if (container && quantity && container.unit !== quantity.unit) {
      return "unit_mismatch";
    }
    if (!quantity) return "container_needs_quantity";
    return null;
  }
  if (field === "container") {
    const container = text(values.container);
    if (container.length > CONTAINER_MAX) return "container_too_long";
    if (!container && completed && task && task.containerRequired) {
      return "container_required";
    }
    return null;
  }
  if (field === "reason") {
    const reason = text(values.reason);
    if (reason.length > REASON_MAX || (!reason && !completed)) {
      return "reason_required";
    }
    return null;
  }
  if (field === "performedAt") {
    const at = values.performedAt || "";
    if (!at || Number.isNaN(new Date(at).getTime())) return "required";
    if (limits && limits.min && at < limits.min) {
      return "performed_at_before_arrival";
    }
    if (limits && limits.max && at > limits.max) return "performed_at_future";
    return null;
  }
  return null;
}

// Whether the form's value differs from the saved one (empty: 422 no_change).
function sameValue(field, a, b) {
  if (field === "performedAt") {
    return (a && new Date(a).getTime()) === (b && new Date(b).getTime());
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

// dryRun `changes` ([{ field: 'result.<field>', from, to }]) -> [{ field, label, from, to }] for
// the preview table, with displays for correctionDialog.vue.
export function previewRows(data, { result, locale } = {}) {
  return ((data && data.changes) || [])
    .map((change) => {
      const match = /^result\.(.+)$/.exec(
        String((change && change.field) || ""),
      );
      const field =
        match && WORKSHOP_CORRECTION_FIELDS.includes(match[1])
          ? match[1]
          : null;
      if (!field) return null;
      return {
        field: change.field,
        label: fieldLabelKey(field, result),
        from: fieldDisplay(field, change.from, { locale }),
        to: fieldDisplay(field, change.to, { locale }),
      };
    })
    .filter(Boolean);
}

// The warnings of a dryRun as i18n keys.
export function previewWarnings(data) {
  return ((data && data.warnings) || [])
    .filter((item) => item && item.code === "estimated_quantity")
    .map(() => "workshop.correction.warnings.estimated_quantity");
}

// The helpers correctionDialog.vue uses for one workshop result instead of the Stage 1 ones (the
// same shape as praCorrections.PRA_FIELDSET): the dialog's `group` is the corrected field.
// now is read on each check, so the performed-at max moves with the clock.
export function workshopFieldset(
  task,
  result,
  {
    arrivalDate = null,
    locale = "it",
    now = () => new Date(),
    wasteEnabled = false,
  } = {},
) {
  const units = (task && task.quantity && task.quantity.units) || [];
  const specs = {
    "quantity.value": { type: "number", label: "workshop.form.quantity" },
    "quantity.unit": {
      type: "select",
      label: "workshop.correction.unit",
      options: units,
    },
    "quantity.basis": {
      type: "select",
      label: "workshop.form.basis",
      options: WORKSHOP_BASES,
      optionKey: "workshop.basis",
    },
    container: wasteEnabled
      ? {
          type: "container",
          label: "workshop.form.container_picker",
          taskCode: task && task.code,
          unit: (result && result.quantity && result.quantity.unit) || null,
          required:
            result &&
            result.result === "completed" &&
            Boolean(task && task.containerRequired),
        }
      : {
          type: "text",
          label: "workshop.form.container",
          max: CONTAINER_MAX,
        },
    reason: {
      type: "text",
      label: fieldLabelKey("reason", result),
      max: REASON_MAX,
    },
    performedAt: { type: "datetime", label: "workshop.form.performed_at" },
  };
  const limits = () => performedAtLimits({ arrivalDate, now: now() });
  const groupFields = (group) =>
    Object.prototype.hasOwnProperty.call(GROUPS, group) ? GROUPS[group] : [];
  return Object.freeze({
    introKey: "workshop.correction.intro",
    groupFields,
    groupLabelKey: (group) => fieldLabelKey(group, result),
    fieldSpec: (path) => specs[path] || { type: "text", label: path },
    // "Attuale: …" under each input.
    valueDisplay: (path, value) => {
      if (path === "quantity.basis" && value) {
        return { key: `workshop.basis.${value}` };
      }
      if (path === "performedAt") {
        return fieldDisplay("performedAt", value ? new Date(value) : null);
      }
      // The saved container id shows as the stored name.
      if (path === "container" && wasteEnabled) {
        return fieldDisplay(
          "container",
          value ? result && result.container : null,
        );
      }
      return fieldDisplay(path, value, { locale });
    },
    visible: () => true,
    changedFields: (group, form, saved) => {
      const changed = !sameValue(
        group,
        correctionValue(group, form, { wasteEnabled }),
        correctionValue(group, saved, { wasteEnabled }),
      );
      return changed ? groupFields(group) : [];
    },
    correctionValue: (group, form) =>
      correctionValue(group, form, { wasteEnabled }),
    inputProblem: (group, form) =>
      correctionProblem(group, form, {
        task,
        result,
        limits: limits(),
        wasteEnabled,
      }),
    // "required" only disables; the rest show as text.
    problemKey: (problem) =>
      problem && problem !== "required"
        ? `workshop.form.errors.${problem}`
        : "",
    dateLimits: (path) => (path === "performedAt" ? limits() : {}),
    previewRows: (data) => previewRows(data, { result, locale }),
    lateWarning: () => null,
    warnings: previewWarnings,
  });
}

// "Valore precedente" of each corrected field of a result, in field order:
// [{ field, label, display, name, date, reason }].
export function previousLines(result, { locale } = {}) {
  const previous = (result && result.previous) || {};
  return WORKSHOP_CORRECTION_FIELDS.filter((field) => previous[field]).map(
    (field) => {
      const item = previous[field];
      return {
        field,
        label: fieldLabelKey(field, result),
        display: fieldDisplay(field, item.from, { locale }),
        name: (item.correctedBy && item.correctedBy.name) || "—",
        date: formatTime(item.correctedAt),
        reason: item.reason || "",
      };
    },
  );
}

// One workshop.corrected change -> { label, lineCode, from, to } for History (the shape of
// corrections.mjs changeLine), or null for result.id / result.taskCode.
export function workshopChangeLine(change, { locale } = {}) {
  const match = /^result\.(.+)$/.exec(String((change && change.field) || ""));
  const field =
    match && WORKSHOP_CORRECTION_FIELDS.includes(match[1]) ? match[1] : null;
  if (!field) return null;
  return {
    label: fieldLabelKey(field, null),
    lineCode: null,
    from: fieldDisplay(field, change.from, { locale }),
    to: fieldDisplay(field, change.to, { locale }),
  };
}

// One commandId per correction: a retry of the same input reuses it, so it is recorded once.
export function correctionSubmitKey({
  checklistId,
  resultId,
  field,
  value,
  reason,
}) {
  return JSON.stringify({ checklistId, resultId, field, value, reason });
}

// The recording codes the API returns for a wrong new value (details.field 'value').
const VALUE_CODES = [
  "reason_required",
  "quantity_required",
  "quantity_unit_invalid",
  "quantity_not_positive",
  "basis_required",
  "quantity_not_allowed",
  "container_required",
  "performed_at_future",
  "performed_at_before_arrival",
  // Stage 6, waste on
  "container_not_found",
  "container_inactive",
  "container_type_not_allowed",
  "unit_mismatch",
  "container_needs_quantity",
  "container_not_allowed",
  "quantity_precision",
];

// A failed correction (not a 409) -> an i18n key, or "" to show the API's errorMsg.
export function correctionErrorKey(err) {
  const data = (err && err.response && err.response.data) || {};
  const details = data.details || {};
  if (data.code === "not_manager")
    return "workshop.correction.errors.not_manager";
  if (data.code === "not_correctable") {
    return "acceptance.corrections.errors.not_correctable";
  }
  if (data.code === "invalid_input" && details.reason === "no_change") {
    return "acceptance.corrections.errors.no_change";
  }
  if (details.field === "value") {
    if (VALUE_CODES.includes(data.code))
      return `workshop.form.errors.${data.code}`;
    if (data.code === "invalid_input")
      return "workshop.correction.errors.invalid_value";
  }
  if (data.code === "reason_required") {
    return "acceptance.corrections.errors.reason_required";
  }
  return data.errorMsg ? "" : "acceptance.corrections.errors.generic";
}

// The text of a failed correction; t is the i18n function.
export function correctionMessage(err, t) {
  const key = correctionErrorKey(err);
  if (key) return t(key);
  const data = (err && err.response && err.response.data) || {};
  return data.errorMsg || "";
}

// The task codes of an arrival-date correction refused by the workshop results (Stage 1 dialog),
// or null for any other error.
export function dateOrderTaskCodes(err) {
  const data = (err && err.response && err.response.data) || {};
  const details = data.details || {};
  if (
    data.code !== "date_order" ||
    details.conflictsWith !== "workshop.performedAt"
  ) {
    return null;
  }
  return Array.isArray(details.taskCodes) ? details.taskCodes : [];
}
